import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { keycloak, effectiveRealmRoles, KC_ROLES, type KcRole } from "./keycloak";
import { signupAsDoctor } from "@/api/authApi";

type AccountType = "patient" | "doctor";

/**
 * True when the user picked "Doctor" on the Keycloak registration form
 * (`account_type` claim, mapped from the `accountType` user attribute) but
 * the JWT does not carry ROLE_DOCTOR yet.
 */
function needsDoctorPromotion(): boolean {
  if (!keycloak.authenticated) return false;
  if (keycloak.tokenParsed?.account_type !== "doctor") return false;
  const roles = effectiveRealmRoles(keycloak.tokenParsed?.realm_access?.roles);
  return !roles.includes(KC_ROLES.DOCTOR) && !roles.includes(KC_ROLES.ADMIN);
}

/** Pre-selects the account type radio on the Keycloak registration form. */
function goToRegistration(redirectUri: string, accountType: AccountType): void {
  const url = new URL(keycloak.createRegisterUrl({ redirectUri }));
  url.searchParams.set("account_type", accountType);
  window.location.assign(url.toString());
}

export interface AuthState {
  initialized: boolean;
  isAuthenticated: boolean;
  token?: string;
  userId?: string;
  username?: string;
  email?: string;
  fullName?: string;
  roles: string[];
  isPatient: boolean;
  isDoctor: boolean;
  isAdmin: boolean;
  /**
   * True while the app is finishing the post-registration doctor-signup
   * promotion (POST /api/auth/doctor/signup + forced token refresh). The SPA
   * should show a spinner rather than kicking the user to /unauthorized
   * while this is happening.
   */
  promotingToDoctor: boolean;
  /**
   * Non-null when the post-registration doctor-signup promotion failed.
   * Surfaced on /unauthorized so the user sees what happened instead of
   * silently staring at a "You can't access this page" message.
   */
  doctorSignupError: string | null;
  login: (redirectPath?: string) => void;
  register: (redirectPath?: string) => void;
  registerAsDoctor: (redirectPath?: string) => void;
  retryDoctorSignup: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Initialises Keycloak (check-sso, no forced login) and exposes auth state.
 *
 * - `check-sso` means public pages render instantly without redirect.
 * - Token is refreshed automatically every 30s if within 60s of expiry.
 * - If the user registered with account type "doctor" but the JWT still
 *   carries only ROLE_PATIENT, we call /api/auth/doctor/signup and force a
 *   token refresh so the JWT carries ROLE_DOCTOR. The choice lives on the
 *   Keycloak user, so it survives new tabs, other devices and cleared
 *   browser data.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [initialized, setInitialized] = useState(false);
  const [tick, setTick] = useState(0);
  const [promotingToDoctor, setPromotingToDoctor] = useState(false);
  const [doctorSignupError, setDoctorSignupError] = useState<string | null>(null);
  const initStarted = useRef(false);
  const promotionTried = useRef(false);

  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;

    keycloak
      .init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri:
          window.location.origin + "/silent-check-sso.html",
        pkceMethod: "S256",
        checkLoginIframe: false,
      })
      .catch((e) => {
        console.error("Sign-in init failed:", e);
      })
      .finally(() => setInitialized(true));

    keycloak.onAuthSuccess = () => setTick((t) => t + 1);
    keycloak.onAuthLogout = () => setTick((t) => t + 1);
    keycloak.onAuthRefreshSuccess = () => setTick((t) => t + 1);
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(60).catch(() => keycloak.login());
    };

    const refreshInterval = window.setInterval(() => {
      if (keycloak.authenticated) {
        keycloak.updateToken(60).catch(() => {
          /* ignore – next call will redirect */
        });
      }
    }, 30_000);

    return () => window.clearInterval(refreshInterval);
  }, []);

  // Runs the two server-side steps that turn a freshly-registered Keycloak
  // user (currently ROLE_PATIENT) into a doctor account:
  //   1. POST /api/auth/doctor/signup — auth-service swaps the realm role
  //      via Keycloak Admin REST.
  //   2. Refresh the access token so the JWT in memory carries ROLE_DOCTOR.
  //
  // We don't rely on keycloak.updateToken alone, because Keycloak's refresh
  // grant can occasionally return a token with stale realm-role claims
  // (the refresh token is bound to the pre-promotion user session). If the
  // refreshed token still reports ROLE_PATIENT, we fall back to
  // keycloak.login(), which re-runs the auth-code flow against the existing
  // SSO session and always returns a fresh JWT reflecting the new roles.
  const runDoctorPromotion = useCallback(async () => {
    setPromotingToDoctor(true);
    setDoctorSignupError(null);
    try {
      await signupAsDoctor();

      let refreshed = false;
      try {
        refreshed = await keycloak.updateToken(-1);
      } catch (refreshErr) {
        console.warn("updateToken failed after doctor signup:", refreshErr);
      }

      const rolesAfter = effectiveRealmRoles(
        keycloak.tokenParsed?.realm_access?.roles,
      );
      if (!rolesAfter.includes(KC_ROLES.DOCTOR)) {
        // Refresh grant returned a stale token (or updateToken silently
        // reused the old access token because minValidity<0 is not
        // universally honoured on all keycloak-js versions). Fall back to
        // a full SSO re-login — this never returns to the app until a new
        // code is exchanged, so we stop here.
        console.info(
          "Refreshed token still missing ROLE_DOCTOR (refreshed=%s); falling back to keycloak.login()",
          refreshed,
        );
        keycloak.login({
          redirectUri: window.location.origin + "/doctor/onboarding",
          prompt: "none",
        });
        return;
      }

      setTick((t) => t + 1);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Unknown error while finishing doctor registration";
      console.error("Doctor signup failed:", err);
      setDoctorSignupError(msg);
    } finally {
      setPromotingToDoctor(false);
    }
  }, []);

  // Once signed in, promote accounts that registered as "doctor" but whose
  // JWT does not carry ROLE_DOCTOR yet.
  useEffect(() => {
    if (!initialized || promotionTried.current || !needsDoctorPromotion()) {
      return;
    }
    promotionTried.current = true;
    void runDoctorPromotion();
  }, [initialized, tick, runDoctorPromotion]);

  const retryDoctorSignup = useCallback(() => {
    promotionTried.current = true;
    void runDoctorPromotion();
  }, [runDoctorPromotion]);

  const login = useCallback((redirectPath?: string) => {
    keycloak.login({
      redirectUri:
        window.location.origin + (redirectPath ?? window.location.pathname),
    });
  }, []);

  const register = useCallback((redirectPath?: string) => {
    goToRegistration(
      window.location.origin + (redirectPath ?? window.location.pathname),
      "patient",
    );
  }, []);

  const registerAsDoctor = useCallback((redirectPath?: string) => {
    goToRegistration(
      window.location.origin + (redirectPath ?? "/doctor/onboarding"),
      "doctor",
    );
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  const value = useMemo<AuthState>(() => {
    const roles = keycloak.authenticated
      ? effectiveRealmRoles(keycloak.tokenParsed?.realm_access?.roles)
      : [];
    const has = (r: KcRole) => roles.includes(r);
    return {
      initialized,
      isAuthenticated: Boolean(keycloak.authenticated),
      token: keycloak.token,
      userId: keycloak.subject,
      username: keycloak.tokenParsed?.preferred_username as string | undefined,
      email: keycloak.tokenParsed?.email as string | undefined,
      fullName: keycloak.tokenParsed?.name as string | undefined,
      roles,
      isPatient: has(KC_ROLES.PATIENT),
      isDoctor: has(KC_ROLES.DOCTOR),
      isAdmin: has(KC_ROLES.ADMIN),
      // Derived synchronously so PrivateRoute shows the spinner on the very
      // first render after init, before the promotion effect has run.
      promotingToDoctor:
        promotingToDoctor || (needsDoctorPromotion() && !doctorSignupError),
      doctorSignupError,
      login,
      register,
      registerAsDoctor,
      retryDoctorSignup,
      logout,
    };
     
  }, [
    initialized,
    tick,
    promotingToDoctor,
    doctorSignupError,
    login,
    register,
    registerAsDoctor,
    retryDoctorSignup,
    logout,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
